import { useTranslation } from "react-i18next";
import { NotesDeluxeSection } from "../notes/notesDeluxeSection";
import { GoogleMapsButton, CopyToClipboard } from "../../Utils";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContactDialog from "../contacts/dialog";
import { ContactsSelector } from "../../utils/moduleSelectors/contactSelector";
import { getContactsAsync, updateContactAsync } from "../../../slices/customer/contact";
import { destroyContact } from "../../../api/customer/contact";
import { fetchBrregOrganization } from "../../../api/external/brreg";

export const CustomerInfo = (props) => {
  const { t } = useTranslation();
  const { item } = props;

  const dispatch = useDispatch();
  const contactsState = useSelector((store) => store.contacts);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [brregMessages, setBrregMessages] = useState([]);

  useEffect(() => {
    if (item?.id) {
      dispatch(
        getContactsAsync({
          page: 1,
          per_page: 50,
          sortBy: "name",
          sort: "ASC",
          filters: [{ name: "CUSTOMER", value: item.id }],
          view: null,
          query: "",
        })
      );
    }
  }, [dispatch, item?.id]);

  const attachedContacts = (contactsState?.data || []).filter(
    (c) => c?.customer?.id === item?.id
  );

  const handleLinkExisting = async (e) => {
    const contactId = e?.target?.value || e?.target?.data?.id || e?.target?.data?.object?.id;
    if (!contactId || !item?.id) return;
    await dispatch(updateContactAsync({ id: contactId, customer_id: item.id }));
    dispatch(
      getContactsAsync({
        page: 1,
        per_page: 50,
        sortBy: "name",
        sort: "ASC",
        filters: [{ name: "CUSTOMER", value: item.id }],
        view: null,
        query: "",
      })
    );
    setSelectorOpen(false);
  };

  const handleUnlink = async (contact) => {
    if (!contact?.id) return;
    await dispatch(updateContactAsync({ id: contact.id, customer_id: null }));
    dispatch(
      getContactsAsync({
        page: 1,
        per_page: 50,
        sortBy: "name",
        sort: "ASC",
        filters: [{ name: "CUSTOMER", value: item.id }],
        view: null,
        query: "",
      })
    );
  };

  const handleDelete = async (contact) => {
    if (!contact?.id) return;
    await destroyContact({ id: contact.id });
    dispatch(
      getContactsAsync({
        page: 1,
        per_page: 50,
        sortBy: "name",
        sort: "ASC",
        filters: [{ name: "CUSTOMER", value: item.id }],
        view: null,
        query: "",
      })
    );
  };

  // Fetch company risk status from Brreg (bankruptcy/liquidation/dissolution) based on VAT
  useEffect(() => {
    const orgnr = (item?.vat || "").toString().replace(/\D/g, "");
    if (!/^\d{9}$/.test(orgnr)) {
      setBrregMessages([]);
      return;
    }
    let mounted = true;
    fetchBrregOrganization(orgnr)
      .then((res) => {
        if (!mounted) return;
        const data = res?.data || {};
        const messages = [];
        // konkurs: true means bankruptcy opened
        if (data?.konkurs === true) {
          messages.push(t("Det er åpnet konkurs i selskapet."));
        }
        // underAvvikling: true means under liquidation
        if (data?.underAvvikling === true) {
          messages.push(t("Selskapet er under avvikling."));
        }
        // underTvangsavviklingEllerTvangsopplosning: true means forced liquidation/dissolution
        if (data?.underTvangsavviklingEllerTvangsopplosning === true) {
          messages.push(t("Selskapet er under tvangsavvikling eller tvangsoppløsning."));
        }
        // Dissolved: look for opphoersdato/slettedato
        if (data?.opphoersdato || data?.slettedato) {
          messages.push(t("Selskapet er avviklet/slettet."));
        }
        setBrregMessages(messages);
      })
      .catch(() => {
        // On error, do not block UI; clear messages
        setBrregMessages([]);
      });
    return () => {
      mounted = false;
    };
  }, [item?.vat, t]);

  return (
    <>
      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div className="grid sm:grid-cols-8 gap-6">
            <div className="sm:col-span-2">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700 mr-2">
                  {t("Active")}:
                </span>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    item?.active
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {item?.active ? t("Yes") : t("No")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {brregMessages?.length > 0 && (
        <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-red-50 px-6 py-4 border-l-4 border-red-500">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.5a.75.75 0 00-1.5 0v5a.75.75 0 001.5 0v-5zM10 13.5a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t("Viktig melding om selskap")}</h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {brregMessages.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">{t("Contact information")}</h3>
              <p className="text-sm text-gray-600">Customer contact details</p>
            </div>
          </div>
        </div>
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Customer Number")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.customer_number || "-"}
                  </p>
                  <CopyToClipboard value={item?.customer_number || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("customer.type")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.customer_type === "private" && "Customer"}
                    {item?.customer_type === "company" && "Firmakunde"}
                    {item?.customer_type === "supplier" && "Leverandør"}
                    {item?.customer_type === "customer_company_supplier" &&
                      "Firmakunde/ Leverandør"}
                    {!item?.customer_type && "-"}
                  </p>
                  <CopyToClipboard
                    value={
                      item?.customer_type === "private"
                        ? "Customer"
                        : item?.customer_type === "company"
                        ? "Firmakunde"
                        : item?.customer_type === "supplier"
                        ? "Leverandør"
                        : item?.customer_type === "customer_company_supplier"
                        ? "Firmakunde/ Leverandør"
                        : ""
                    }
                  />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Name")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.name || "-"}
                  </p>
                  <CopyToClipboard value={item?.email || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Email")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.email || "-"}
                  </p>
                  <CopyToClipboard value={item?.email || ""} />
                </div>
              </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Mobile")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.mobile || "-"}
                  </p>
                  <CopyToClipboard value={item?.mobile || ""} />
                </div>
              </div>
            </div>

            {(item?.customer_type === "company" ||
              item?.customer_type === "customer_company_supplier") && (
              <div className="col-span-6 sm:col-span-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Org. Nummer")}
                  </label>
                  <div className="flex items-center">
                    <p className="text-sm text-gray-900 mr-2">
                      {item?.vat || "-"}
                    </p>
                    <CopyToClipboard value={item?.vat || ""} />
                  </div>
                </div>
              </div>
            )}

            <div className="col-span-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Short Description")}
                </label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">
                    {item?.short_description || "-"}
                  </p>
                  <CopyToClipboard value={item?.short_description || ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11c1.657 0 3-1.343 3-3S17.657 5 16 5s-3 1.343-3 3 1.343 3 3 3zM8 11c1.657 0 3-1.343 3-3S9.657 5 8 5 5 6.343 5 8s1.343 3 3 3zm0 2c-2.21 0-4 1.79-4 4v1h8v-1c0-2.21-1.79-4-4-4zm8 0c-.74 0-1.424.201-2.02.553A4.978 4.978 0 0118 17v1h6v-1c0-2.21-1.79-4-4-4z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{t("Contacts")}</h3>
                <p className="text-sm text-gray-600">Linked contacts for this customer</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center rounded-md border border-transparent bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                onClick={() => setSelectorOpen(true)}
              >
                {t("Add contact")}
              </button>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          {selectorOpen && (
            <div className="mb-4">
              <ContactsSelector
                open={selectorOpen}
                module="customer"
                module_param={item?.id}
                onChange={handleLinkExisting}
                onClose={() => setSelectorOpen(false)}
              />
            </div>
          )}

          {attachedContacts?.length < 1 ? (
            <p className="text-sm text-gray-500">{t("No contacts linked to this customer.")}</p>
          ) : (
            <ul role="list" className="divide-y divide-gray-200">
              {attachedContacts.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.displayTitle || c.name}</p>
                    <p className="text-sm text-gray-500">{c.email || "-"} {c.mobile ? `· ${c.mobile}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      onClick={() => { setSelectedContact(c); setContactDialogOpen(true); }}
                    >
                      {t("Edit")}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      onClick={() => handleUnlink(c)}
                    >
                      {t("Unlink")}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-red-700"
                      onClick={() => handleDelete(c)}
                    >
                      {t("Delete")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-6">
        {/* Postal Address Card */}
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">{t("Postal Address")}</h3>
                  <p className="text-sm text-gray-600">Customer postal address</p>
                </div>
              </div>
              <GoogleMapsButton
                address={
                  item?.postal_address || {
                    address: item?.address,
                    address2: item?.address_two,
                    postal: item?.postal,
                    place: item?.place,
                  }
                }
              />
            </div>
          </div>
          <div className="bg-white px-6 py-6">
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-6">
              <div className="col-span-3 sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Address")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.postal_address?.address || item?.address || "-"}</p>
                  <CopyToClipboard value={item?.postal_address?.address || item?.address || ""} />
                </div>
              </div>
              <div className="col-span-3 sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Address 2")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.postal_address?.address2 || item?.address_two || "-"}</p>
                  <CopyToClipboard value={item?.postal_address?.address2 || item?.address_two || ""} />
                </div>
              </div>
              <div className="col-span-1 sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Postal")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.postal_address?.postal || item?.postal || "-"}</p>
                  <CopyToClipboard value={item?.postal_address?.postal || item?.postal || ""} />
                </div>
              </div>
              <div className="col-span-2 sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Place")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.postal_address?.place || item?.place || "-"}</p>
                  <CopyToClipboard value={item?.postal_address?.place || item?.place || ""} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Physical Address Card */}
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">{t("Physical Address")}</h3>
                  <p className="text-sm text-gray-600">Customer physical address</p>
                </div>
              </div>
              <GoogleMapsButton address={item?.physical_address} />
            </div>
          </div>
          <div className="bg-white px-6 py-6">
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-6">
              <div className="col-span-3 sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Address")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.physical_address?.address || "-"}</p>
                  <CopyToClipboard value={item?.physical_address?.address || ""} />
                </div>
              </div>
              <div className="col-span-3 sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Address 2")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.physical_address?.address2 || "-"}</p>
                  <CopyToClipboard value={item?.physical_address?.address2 || ""} />
                </div>
              </div>
              <div className="col-span-1 sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Postal")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.physical_address?.postal || "-"}</p>
                  <CopyToClipboard value={item?.physical_address?.postal || ""} />
                </div>
              </div>
              <div className="col-span-2 sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Place")}</label>
                <div className="flex items-center">
                  <p className="text-sm text-gray-900 mr-2">{item?.physical_address?.place || "-"}</p>
                  <CopyToClipboard value={item?.physical_address?.place || ""} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="shadow sm:overflow-hidden sm:rounded-md mt-5">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div className="grid sm:grid-cols-8 gap-6">
            <div className="sm:col-span-8">
              <NotesDeluxeSection module={"customer"} module_param={item?.id} />
            </div>
          </div>
        </div>
      </div>

      <ContactDialog
        open={contactDialogOpen}
        onClose={() => { setContactDialogOpen(false); setSelectedContact(null); }}
        selected={selectedContact}
        onRefresh={() => {
          dispatch(
            getContactsAsync({
              page: 1,
              per_page: 50,
              sortBy: "name",
              sort: "ASC",
              filters: [{ name: "CUSTOMER", value: item.id }],
              view: null,
              query: "",
            })
          );
        }}
      />
    </>
  );
};
